/**
 * AnnotationToolbar 컴포넌트
 * Annotation 도구 선택 및 설정 UI
 */

import React from 'react';
import {
  AnnotationTool,
  UnderlineStyle,
  HIGHLIGHTER_COLORS,
  UNDERLINE_COLORS,
  ANNOTATION_DEFAULTS,
} from '../../types/Annotation';
import { useI18n } from '../../contexts/I18nContext';
import { useDialog } from '../../contexts/DialogContext';

export interface AnnotationToolbarProps {
  /** 현재 선택된 도구 */
  currentTool: AnnotationTool;
  /** 현재 색상 */
  currentColor: string;
  /** 현재 선 두께 */
  currentStrokeWidth: number;
  /** 현재 투명도 (형광펜용) */
  currentOpacity: number;
  /** 현재 밑줄 스타일 */
  currentUnderlineStyle: UnderlineStyle;
  /** 도구 변경 콜백 */
  onToolChange: (tool: AnnotationTool) => void;
  /** 색상 변경 콜백 */
  onColorChange: (color: string) => void;
  /** 선 두께 변경 콜백 */
  onStrokeWidthChange: (width: number) => void;
  /** 투명도 변경 콜백 */
  onOpacityChange: (opacity: number) => void;
  /** 밑줄 스타일 변경 콜백 */
  onUnderlineStyleChange: (style: UnderlineStyle) => void;
  /** 전체 지우기 콜백 */
  onClearAll: () => void;
  /** 닫기 콜백 */
  onClose: () => void;
  /** 스테이지 스케일 */
  stageScale?: number;
  /** 툴바 위치 (클릭한 마우스 위치 기준) */
  position?: { x: number; y: number };
}

const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  currentTool,
  currentColor,
  currentStrokeWidth,
  currentOpacity,
  currentUnderlineStyle,
  onToolChange,
  onColorChange,
  onStrokeWidthChange,
  onOpacityChange,
  onUnderlineStyleChange,
  onClearAll,
  onClose,
  stageScale = 1,
  position,
}) => {
  const { t } = useI18n();
  const dialog = useDialog();
  // 현재 도구에 따른 색상 팔레트
  const colorPalette =
    currentTool === 'underline' ? UNDERLINE_COLORS : HIGHLIGHTER_COLORS;

  // 두께 범위 (도구에 따라 다름)
  const strokeWidthRange =
    currentTool === 'underline'
      ? { min: 1, max: 10, step: 1 }
      : { min: 10, max: 50, step: 5 };

  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    top: position ? position.y - 100 : -98,
    left: position ? position.x : 0,
    transform: position ? 'translateX(-50%)' : 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    zIndex: 99999,
    fontSize: '12px',
    whiteSpace: 'nowrap',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  };

  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#E5E7EB',
  };

  const separatorStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: '#E5E7EB',
    margin: '0 4px',
  };

  const colorButtonStyle = (color: string, isSelected: boolean): React.CSSProperties => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: color,
    border: isSelected ? '2px solid #3B82F6' : '1px solid #D1D5DB',
    cursor: 'pointer',
    transition: 'transform 0.15s',
  });

  return (
    <div style={toolbarStyle} onClick={(e) => e.stopPropagation()}>
      {/* 형광펜 버튼 */}
      <button
        style={currentTool === 'highlighter' ? activeButtonStyle : buttonStyle}
        onClick={() => onToolChange('highlighter')}
        title={t.annotation.highlighter}
        onMouseEnter={(e) => {
          if (currentTool !== 'highlighter') {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }
        }}
        onMouseLeave={(e) => {
          if (currentTool !== 'highlighter') {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          <rect x="2" y="17" width="6" height="4" rx="1" fill={currentTool === 'highlighter' ? currentColor : '#FDE047'} opacity="0.6" />
        </svg>
      </button>

      {/* 밑줄 버튼 */}
      <button
        style={currentTool === 'underline' ? activeButtonStyle : buttonStyle}
        onClick={() => onToolChange('underline')}
        title={t.annotation.underline}
        onMouseEnter={(e) => {
          if (currentTool !== 'underline') {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }
        }}
        onMouseLeave={(e) => {
          if (currentTool !== 'underline') {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
          <line x1="4" y1="21" x2="20" y2="21" stroke={currentTool === 'underline' ? currentColor : 'currentColor'} strokeWidth="3" />
        </svg>
      </button>

      {/* 지우개 버튼 */}
      <button
        style={currentTool === 'eraser' ? activeButtonStyle : buttonStyle}
        onClick={() => onToolChange('eraser')}
        title={t.annotation.eraser}
        onMouseEnter={(e) => {
          if (currentTool !== 'eraser') {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }
        }}
        onMouseLeave={(e) => {
          if (currentTool !== 'eraser') {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 20H7L3 16l10-10 8 8-4 4" />
          <path d="m18 12-8 8" />
        </svg>
      </button>

      <div style={separatorStyle} />

      {/* 색상 팔레트 */}
      {(currentTool === 'highlighter' || currentTool === 'underline') && (
        <>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {colorPalette.map((color) => (
              <button
                key={color}
                style={colorButtonStyle(color, currentColor === color)}
                onClick={() => onColorChange(color)}
                title={color}
              />
            ))}
          </div>

          <div style={separatorStyle} />

          {/* 두께 슬라이더 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#6B7280', fontSize: '11px' }}>{t.annotation.size}</span>
            <input
              type="range"
              min={strokeWidthRange.min}
              max={strokeWidthRange.max}
              step={strokeWidthRange.step}
              value={currentStrokeWidth}
              onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
              style={{ width: '60px', accentColor: '#3B82F6' }}
            />
            <span style={{ color: '#374151', fontSize: '11px', minWidth: '24px' }}>
              {currentStrokeWidth}
            </span>
          </div>

          {/* 형광펜일 때만 투명도 표시 */}
          {currentTool === 'highlighter' && (
            <>
              <div style={separatorStyle} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ color: '#6B7280', fontSize: '11px' }}>{t.annotation.opacity}</span>
                <input
                  type="range"
                  min={0.1}
                  max={0.8}
                  step={0.1}
                  value={currentOpacity}
                  onChange={(e) => onOpacityChange(Number(e.target.value))}
                  style={{ width: '50px', accentColor: '#3B82F6' }}
                />
                <span style={{ color: '#374151', fontSize: '11px', minWidth: '24px' }}>
                  {Math.round(currentOpacity * 100)}%
                </span>
              </div>
            </>
          )}

          {/* 밑줄일 때만 스타일 표시 */}
          {currentTool === 'underline' && (
            <>
              <div style={separatorStyle} />
              <div style={{ display: 'flex', gap: '2px' }}>
                <button
                  style={{
                    ...buttonStyle,
                    width: '28px',
                    height: '28px',
                    backgroundColor: currentUnderlineStyle === 'solid' ? '#E5E7EB' : 'transparent',
                  }}
                  onClick={() => onUnderlineStyleChange('solid')}
                  title={t.annotation.solid}
                >
                  <svg width="16" height="4" viewBox="0 0 16 4">
                    <line x1="0" y1="2" x2="16" y2="2" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
                <button
                  style={{
                    ...buttonStyle,
                    width: '28px',
                    height: '28px',
                    backgroundColor: currentUnderlineStyle === 'dashed' ? '#E5E7EB' : 'transparent',
                  }}
                  onClick={() => onUnderlineStyleChange('dashed')}
                  title={t.annotation.dashed}
                >
                  <svg width="16" height="4" viewBox="0 0 16 4">
                    <line x1="0" y1="2" x2="16" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2" />
                  </svg>
                </button>
                <button
                  style={{
                    ...buttonStyle,
                    width: '28px',
                    height: '28px',
                    backgroundColor: currentUnderlineStyle === 'wavy' ? '#E5E7EB' : 'transparent',
                  }}
                  onClick={() => onUnderlineStyleChange('wavy')}
                  title={t.annotation.wavy}
                >
                  <svg width="16" height="8" viewBox="0 0 16 8">
                    <path d="M0 4 Q2 0, 4 4 T8 4 T12 4 T16 4" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </>
      )}

      <div style={separatorStyle} />

      {/* 전체 지우기 버튼 */}
      <button
        style={buttonStyle}
        onClick={async () => {
          if (await dialog.confirm(t.annotation.deleteConfirm)) {
            onClearAll();
          }
        }}
        title={t.annotation.clearAll}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#FEE2E2';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>

      {/* 닫기 버튼 */}
      <button
        style={buttonStyle}
        onClick={onClose}
        title={t.annotation.close}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F3F4F6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default AnnotationToolbar;
